import useAuthenticatedUser from 'packages/kakapo-ui-application/hooks/useAuthenticatedUser';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import { useTranslation } from 'react-i18next';
import * as graphql from 'generated/graphql';
import { useField } from 'formik';
import { Heading, Paragraph } from '../../../packages/@treams/treams-ui/src/Text';
import { QuillText, Tags } from '@treams/treams-ui';
import { sortBy } from 'lodash';

export default function CompetencyPicker({
	onChange,
}: {
	onChange?: (value: graphql.Competency) => void;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const authenticatedUser = useAuthenticatedUser();

	const { data } = graphql.useJobPositionsQuery({
		variables: {
			id: authenticatedUser.id,
			status: graphql.JobPositionStatus.Active,
		},
	});

	const [fetch, query] = graphql.useCompetenciesLazyQuery({
		variables: {
			isArchived: false,
			jobTitleIDs: data?.user.jobPositions.map((jobPosition) => jobPosition.jobTitle.id),
			first: 1000,
		},
	});

	const [{ value }, , { setValue }] = useField<graphql.CompetencyGoal>('competency');

	return (
		<Tags.Item.Select
			label={t('goals.show.competency.label')}
			options={
				query.data &&
				sortBy(
					query.data.competencies.edges
						.filter(({ node }) => node.attachableToGoal)
						.map(({ node }) => ({
							value: node.id,
							title: localize(node.title).text,
							context: (
								<>
									<Heading>{localize(node.title).text}</Heading>
									<Paragraph mt={2}>
										<QuillText quill={localize(node.description ?? [])?.text} />
									</Paragraph>
								</>
							),
						})),
					'title',
				)
			}
			onChange={(value) => {
				const competency = query
					.data!.competencies.edges.map(({ node }) => node)
					.find((node) => node.id === value)! as graphql.Competency;
				setValue({ competency, competencyLevelIndex: 0, appraisals: [] });

				onChange && onChange(competency);
			}}
			fetch={fetch}
			selectedOption={
				value
					? {
							value: value.competency.id,
							title: localize(value.competency.title).text,
					  }
					: undefined
			}
		/>
	);
}
